import { useState } from "react";
import { toast } from "sonner";
import { Search, Sparkles, Copy, Loader2, TrendingUp, ChevronDown, HelpCircle } from "lucide-react";
import api from "@/lib/api";

const PUBLISH_GUIDES = {
  wordpress: {
    label: "WordPress",
    steps: [
      "Install a free SEO plugin (Yoast SEO or Rank Math) if you don't have one.",
      "Edit your page/post → scroll to the SEO plugin box → paste the Title into 'SEO title' and the Description into 'Meta description'.",
      "For Open Graph, use the plugin's 'Social' tab (Yoast) to set the social title/description/image.",
      "For JSON-LD schema, paste the <script> block into a Custom HTML block in the page, or use the plugin's Schema settings.",
      "Add alt text by clicking each image in the editor → 'Alt text' field.",
    ],
  },
  wix: {
    label: "Wix",
    steps: [
      "Go to your Wix dashboard → Marketing & SEO → SEO → 'Go to SEO Settings' for the page.",
      "Paste the Title into 'Title tag' and the Description into 'Meta description'.",
      "Open 'Social Share' to set the Open Graph title, description and image.",
      "For JSON-LD, use SEO Settings → 'Advanced SEO' → 'Additional tags' → 'Add new tag' → Custom → paste the <script> block.",
      "Add alt text via each image's Settings → 'What's in the image? (Alt text)'.",
    ],
  },
  squarespace: {
    label: "Squarespace",
    steps: [
      "Go to Pages → click the gear on the page → 'SEO' tab.",
      "Paste the Title into 'SEO Title' and the Description into 'SEO Description'.",
      "Social image is set under the same SEO panel ('Social Image').",
      "For JSON-LD, add a Code Block to the page and paste the <script> block (Settings → Advanced → allows code injection).",
      "Add alt text in each image block → 'Design' → alt text / caption.",
    ],
  },
  shopify: {
    label: "Shopify",
    steps: [
      "Go to the product/page → scroll to 'Search engine listing' → 'Edit'.",
      "Paste the Title into 'Page title' and the Description into 'Meta description'.",
      "For Open Graph & JSON-LD, edit your theme (Online Store → Themes → Edit code) and add the tags to theme.liquid <head>, or use an SEO app.",
      "Add alt text in the product images area → click an image → 'Add alt text'.",
    ],
  },
  generic: {
    label: "Any HTML site",
    steps: [
      "Open your page's HTML and locate the <head> section.",
      "Paste the <title> and the <meta name=\"description\"> tags inside <head> (replace any existing ones).",
      "Paste the Open Graph <meta property=\"og:…\"> tags inside <head> too.",
      "Paste the JSON-LD <script> block just before </head> or </body>.",
      "Set the H1 as the main heading of the page, and add alt=\"…\" to each <img> tag.",
    ],
  },
};

function detectPlatform(platform) {
  const p = (platform || "").toLowerCase();
  if (p.includes("wordpress")) return "wordpress";
  if (p.includes("wix")) return "wix";
  if (p.includes("squarespace")) return "squarespace";
  if (p.includes("shopify")) return "shopify";
  return "generic";
}

const SEV_WEIGHT = { critical: 30, high: 18, warning: 10, info: 3 };
const SEO_ADDRESSED = new Set([
  "seo-no-title", "seo-title-long", "seo-title-short", "seo-no-description",
  "seo-description-long", "seo-no-h1", "seo-multiple-h1", "seo-no-open-graph",
  "seo-no-structured-data", "seo-images-missing-alt", "seo-no-canonical", "seo-no-lang",
]);

function computeLift(seoResult) {
  if (!seoResult || typeof seoResult.score !== "number") return null;
  const flags = seoResult.flags || [];
  const remaining = flags
    .filter((f) => !SEO_ADDRESSED.has(f.id))
    .reduce((a, f) => a + (SEV_WEIGHT[f.severity] || 0), 0);
  const fixes = flags.filter((f) => SEO_ADDRESSED.has(f.id)).length;
  return { current: seoResult.score, projected: Math.max(0, Math.min(100, 100 - remaining)), fixes };
}

function ScoreLift({ lift }) {
  if (!lift || lift.fixes === 0 || lift.projected <= lift.current) return null;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[#32D74B]/25 bg-[#32D74B]/5 p-3" data-testid="auto-seo-score-lift">
      <TrendingUp size={18} className="text-[#32D74B] shrink-0" />
      <div className="text-center px-1">
        <div className="text-lg font-bold text-zinc-400 font-mono">{lift.current}</div>
        <div className="text-[9px] uppercase tracking-wider text-zinc-500">Now</div>
      </div>
      <span className="text-[#32D74B] text-lg">→</span>
      <div className="text-center px-1">
        <div className="text-lg font-bold text-[#32D74B] font-mono">{lift.projected}</div>
        <div className="text-[9px] uppercase tracking-wider text-zinc-500">Projected</div>
      </div>
      <p className="text-xs text-zinc-300 ml-1">
        Applying these fixes could lift your SEO score to <b className="text-[#32D74B]">{lift.projected}/100</b> — a <b className="text-[#32D74B]">+{lift.projected - lift.current}</b> point gain across {lift.fixes} fix{lift.fixes > 1 ? "es" : ""}.
      </p>
    </div>
  );
}

function CopyRow({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-black/25 p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</span>
        <button
          onClick={() => { navigator.clipboard?.writeText(value); toast.success(`${label} copied`); }}
          data-testid={`${testid}-copy`}
          className="text-zinc-400 hover:text-white transition-colors" title={`Copy ${label}`}>
          <Copy size={13} />
        </button>
      </div>
      <pre className="text-[11.5px] text-[#8fe3ff] font-mono whitespace-pre-wrap break-words leading-relaxed">{value}</pre>
    </div>
  );
}

export default function AutoSeoCard({ domain, initial, seoResult, platform }) {
  const [seo, setSeo] = useState(initial || null);
  const [loading, setLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const lift = computeLift(seoResult);
  const guide = PUBLISH_GUIDES[detectPlatform(platform)];

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/seo/auto/${encodeURIComponent(domain)}`);
      if (res.data.unavailable || !res.data.seo) {
        toast.error("AI is temporarily unavailable — please try again shortly.");
      } else {
        setSeo(res.data.seo);
        if (!res.data.cached) toast.success("Auto SEO fixes generated");
      }
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Auto SEO is included with the $29 report or the Pro plan.");
      else if (e?.response?.status === 404) toast.error("Scan the domain first.");
      else toast.error("Could not generate SEO fixes right now.");
    } finally { setLoading(false); }
  };

  const ogBlock = seo?.openGraph?.length
    ? seo.openGraph.map((t) => `<meta property="${t.property}" content="${t.content}" />`).join("\n")
    : "";
  const titleBlock = seo?.title ? `<title>${seo.title}</title>` : "";
  const descBlock = seo?.description ? `<meta name="description" content="${seo.description}" />` : "";

  return (
    <div className="bg-[#121212] border border-[#7c5cff]/30 rounded-xl p-5 mb-6" data-testid="auto-seo-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <Search size={16} className="text-[#a78bfa]" />
          <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Auto SEO Fixes</h3>
        </div>
        {seo && (
          <button onClick={generate} disabled={loading} data-testid="auto-seo-regenerate-btn"
            className="inline-flex items-center gap-1.5 bg-white/5 border border-white/15 hover:bg-white/10 text-white rounded-lg px-3 py-1.5 text-xs transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Regenerate
          </button>
        )}
      </div>

      {!seo ? (
        <>
          <p className="text-zinc-500 text-sm mb-3">Let AI read your page and write copy-paste-ready SEO fixes — an optimized title, meta description, H1, Open Graph tags, JSON-LD schema and image alt-text.</p>
          <ScoreLift lift={lift} />
          <button onClick={generate} disabled={loading} data-testid="auto-seo-generate-btn"
            className="inline-flex items-center gap-2 btn-brand text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-60 mt-3">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            {loading ? "Generating…" : "Generate Auto SEO Fixes"}
          </button>
        </>
      ) : (
        <div className="space-y-3 mt-2">
          <ScoreLift lift={lift} />
          {seo.notes && <p className="text-zinc-400 text-xs italic">{seo.notes}</p>}
          <CopyRow label="Title tag" value={titleBlock} testid="auto-seo-title" />
          <CopyRow label="Meta description" value={descBlock} testid="auto-seo-description" />
          {seo.h1 && <CopyRow label="H1 heading" value={seo.h1} testid="auto-seo-h1" />}
          {ogBlock && <CopyRow label="Open Graph tags" value={ogBlock} testid="auto-seo-og" />}
          {seo.jsonLd && <CopyRow label="JSON-LD structured data" value={seo.jsonLd} testid="auto-seo-jsonld" />}
          {Array.isArray(seo.altText) && seo.altText.length > 0 && (
            <div className="rounded-lg border border-white/10 bg-black/25 p-3" data-testid="auto-seo-alt">
              <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Image alt-text suggestions</span>
              <ul className="list-disc list-inside mt-1.5 space-y-1 text-xs text-zinc-300">
                {seo.altText.map((a, i) => <li key={i} className="leading-relaxed">{a}</li>)}
              </ul>
            </div>
          )}

          <button onClick={() => setShowGuide((s) => !s)} data-testid="auto-seo-guide-toggle"
            className="inline-flex items-center gap-1.5 text-xs text-[#a78bfa] hover:text-white transition-colors">
            <HelpCircle size={13} /> Where do I paste these? ({guide.label})
            <ChevronDown size={13} className={`transition-transform ${showGuide ? "rotate-180" : ""}`} />
          </button>
          {showGuide && (
            <div className="rounded-lg border border-[#7c5cff]/20 bg-black/20 p-3" data-testid="auto-seo-guide">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Steps for {guide.label}</div>
              <ol className="list-decimal list-inside space-y-1.5 text-xs text-zinc-300">
                {guide.steps.map((s, i) => <li key={i} className="leading-relaxed">{s}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}
      <p className="text-zinc-600 text-[11px] mt-3">Review and adapt the copy to your brand voice before publishing. AI suggestions — verify against your keywords and page content.</p>
    </div>
  );
}
